import { useTranslation } from 'react-i18next'
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import { useUser, User } from 'context/UserContext'
import { useAutoRedirect } from 'hooks/useAutoRedirect'
import { RoutePaths } from 'route-paths'
import { FeedbackPersonalize } from 'pages/FeedbackPersonalize'
import { Gender } from 'pages/Gender'
import { Goals } from 'pages/Goals'
import { GoalSelection } from 'pages/GoalSelection'
import { FeedbackGoalset } from 'pages/FeedbackGoalset'
import { FeedbackGoalChoice } from 'pages/FeedbackGoalChoice'
import { Height } from 'pages/Height'
import { Weight } from 'pages/Weight'
import { GoalWeight } from 'pages/GoalWeight'
import { FeedbackGoalWeight } from 'pages/FeedbackGoalWeight'
import { FitnessLevel } from 'pages/FitnessLevel'
import { Age } from 'pages/Age'
import { FeedbackTenMinutes } from 'pages/FeedbackTenMinutes'
import { Password } from 'pages/Password'
import { Email } from 'pages/Email'
import { Login } from 'pages/Login'
import { ForgotPassword } from 'pages/ForgotPassword'
import { PasswordReset } from 'pages/PasswordReset'
import { Loading } from 'pages/Loading'
import { ThankYou } from 'pages/ThankYou'
import { StripeThankYou } from 'pages/Stripe/ThankYou'
import { MagicLogin } from 'components/MagicLogin'
import { GiftCard } from 'pages/GiftCard'
import { Statement } from 'pages/Statement'
import { CancellationRequest } from 'pages/CancellationRequest'
import { CancellationRequestSuccess } from 'pages/CancellationRequestSuccess'
import { FeedbackExerciseRoutine } from 'pages/FeedbackExerciseRoutine'
import { ExerciseRoutine } from 'pages/ExerciseRoutine'
import { AgingQuestion1 } from 'pages/AgingQuestion1'
import { FeedbackAging } from 'pages/FeedbackAging'
import { Pace } from 'pages/Pace'
import { Review } from 'pages/Review'
import { HowOftenExercise } from 'pages/HowOftenExercise'
import { HowFeelExercise } from 'pages/HowFeelExercise'
import { ExperienceYoga } from 'pages/ExperienceYoga'
import { FeedbackExperience } from 'pages/FeedbackExperience'
import { Mindstate } from 'pages/Mindstate'
import { ExerciseTime } from 'pages/ExerciseTime'
import { Diet } from 'pages/Diet'
import { FeedbackExperts } from 'pages/FeedbackExperts'
import { FeedbackRisks } from 'pages/FeedbackRisks'
import { ForgotPasswordSuccess } from 'pages/ForgotPasswordSuccess'
import { MeditationMovement } from 'pages/MeditationMovement'
import { MeditationBalance } from 'pages/MeditationBalance'
import { MeditationMind } from 'pages/MeditationMind'
import { MeditationSleep } from 'pages/MeditationSleep'
import { MeditationEnergy } from 'pages/MeditationEnergy'
import { MeditationFatigue } from 'pages/MeditationFatigue'
import { MeditationPlan } from 'pages/MeditationPlan'
import { Checkout } from 'pages/Stripe/Checkout'
import { Plans } from 'pages/Stripe/Plans'

interface Props {
  locale: string
}

const ProtectedRoute = ({
  user,
  redirectPath = RoutePaths.Login,
}: {
  user: User | undefined
  redirectPath?: string
}) => {
  if (!user?.authToken || !user.email) {
    return <Navigate to={redirectPath} replace />
  }

  return <Outlet />
}

const cancellationRedirectPath = () => {
  const redirectPath = window.location.pathname + window.location.search
  return `${RoutePaths.Login}?redirect_path=${redirectPath}`
}

export const FunnelRouter: React.FC<Props> = ({ locale }) => {
  const { t } = useTranslation('translation', {
    useSuspense: false,
  })

  const { user } = useUser()

  useAutoRedirect()

  return (
    <Routes>
      <Route
        path={RoutePaths.Root}
        element={<Navigate to={RoutePaths.Gender} />}
      />
      <Route
        path={RoutePaths.Gender}
        element={<Gender nextRoute={RoutePaths.ExerciseRoutine} />}
      />
      <Route
        path={RoutePaths.ExerciseRoutine}
        element={
          <ExerciseRoutine nextRoute={RoutePaths.FeedbackExerciseRoutine} />
        }
      />
      <Route
        path={RoutePaths.FeedbackExerciseRoutine}
        element={<FeedbackExerciseRoutine nextRoute={RoutePaths.Goals} />}
      />
      <Route
        path={RoutePaths.Goals}
        element={<Goals nextRoute={RoutePaths.GoalSelection} />}
      />
      <Route
        path={RoutePaths.GoalSelection}
        element={<GoalSelection nextRoute={RoutePaths.FeedbackGoalset} />}
      />
      <Route
        path={RoutePaths.FeedbackGoalset}
        element={<FeedbackGoalset nextRoute={RoutePaths.FeedbackGoalChoice} />}
      />
      <Route
        path={RoutePaths.FeedbackGoalChoice}
        element={<FeedbackGoalChoice nextRoute={RoutePaths.Age} />}
      />
      <Route
        path={RoutePaths.Age}
        element={<Age nextRoute={RoutePaths.Height} />}
      />
      <Route
        path={RoutePaths.Height}
        element={<Height nextRoute={RoutePaths.Weight} />}
      />
      <Route
        path={RoutePaths.Weight}
        element={<Weight nextRoute={RoutePaths.GoalWeight} />}
      />
      <Route
        path={RoutePaths.GoalWeight}
        element={<GoalWeight nextRoute={RoutePaths.FeedbackGoalWeight} />}
      />
      <Route
        path={RoutePaths.FeedbackGoalWeight}
        element={<FeedbackGoalWeight nextRoute={RoutePaths.AgingQuestion1} />}
      />
      <Route
        path={RoutePaths.AgingQuestion1}
        element={<AgingQuestion1 nextRoute={RoutePaths.FeedbackAging} />}
      />
      <Route
        path={RoutePaths.FeedbackAging}
        element={<FeedbackAging nextRoute={RoutePaths.Pace} />}
      />
      <Route
        path={RoutePaths.Pace}
        element={<Pace nextRoute={RoutePaths.Review} />}
      />
      <Route
        path={RoutePaths.Review}
        element={<Review nextRoute={RoutePaths.HowOftenExercise} />}
      />
      <Route
        path={RoutePaths.HowOftenExercise}
        element={<HowOftenExercise nextRoute={RoutePaths.HowFeelExercise} />}
      />
      <Route
        path={RoutePaths.HowFeelExercise}
        element={<HowFeelExercise nextRoute={RoutePaths.StatementTwo} />}
      />
      <Route
        path={RoutePaths.StatementTwo}
        element={
          <Statement
            text={t('statements.two')}
            nextRoute={RoutePaths.StatementThree}
          />
        }
      />
      <Route
        path={RoutePaths.StatementThree}
        element={
          <Statement
            text={t('statements.three')}
            nextRoute={RoutePaths.StatementFour}
          />
        }
      />
      <Route
        path={RoutePaths.StatementFour}
        element={
          <Statement
            text={t('statements.four')}
            nextRoute={RoutePaths.ExperienceYoga}
          />
        }
      />
      <Route
        path={RoutePaths.ExperienceYoga}
        element={<ExperienceYoga nextRoute={RoutePaths.FeedbackExperience} />}
      />
      <Route
        path={RoutePaths.FeedbackExperience}
        element={<FeedbackExperience nextRoute={RoutePaths.FitnessLevel} />}
      />
      <Route
        path={RoutePaths.FitnessLevel}
        element={<FitnessLevel nextRoute={RoutePaths.Mindstate} />}
      />
      <Route
        path={RoutePaths.Mindstate}
        element={<Mindstate nextRoute={RoutePaths.ExerciseTime} />}
      />
      <Route
        path={RoutePaths.ExerciseTime}
        element={<ExerciseTime nextRoute={RoutePaths.FeedbackTenMinutes} />}
      />
      <Route
        path={RoutePaths.FeedbackTenMinutes}
        element={<FeedbackTenMinutes nextRoute={RoutePaths.Diet} />}
      />
      <Route
        path={RoutePaths.Diet}
        element={<Diet nextRoute={RoutePaths.FeedbackExperts} />}
      />
      <Route
        path={RoutePaths.FeedbackExperts}
        element={<FeedbackExperts nextRoute={RoutePaths.FeedbackRisks} />}
      />
      <Route
        path={RoutePaths.FeedbackRisks}
        element={<FeedbackRisks nextRoute={RoutePaths.Password} />}
      />
      <Route
        path={RoutePaths.Password}
        element={<Password nextRoute={RoutePaths.Email} />}
      />
      <Route
        path={RoutePaths.Email}
        element={<Email locale={locale} nextRoute={RoutePaths.Loading} />}
      />
      <Route path={RoutePaths.Login} element={<Login locale={locale} />} />
      <Route
        path={RoutePaths.ForgotPassword}
        element={
          <ForgotPassword
            locale={locale}
            nextRoute={RoutePaths.ForgotPasswordSuccess}
          />
        }
      />
      <Route
        path={RoutePaths.ForgotPasswordSuccess}
        element={<ForgotPasswordSuccess />}
      />
      <Route
        path={RoutePaths.PasswordReset}
        element={<PasswordReset locale={locale} />}
      />
      <Route
        path={RoutePaths.MagicLogin}
        element={<MagicLogin locale={locale} />}
      />
      <Route element={<ProtectedRoute user={user} />}>
        <Route
          path={RoutePaths.Loading}
          element={<Loading locale={locale} />}
        />
        <Route
          path={RoutePaths.Plans}
          element={<Plans locale={locale} nextRoute={RoutePaths.Checkout} />}
        />
        <Route
          path={RoutePaths.Checkout}
          element={
            <Checkout
              locale={locale}
              nextRoute={RoutePaths.MeditationMovement}
            />
          }
        />
        <Route
          path={RoutePaths.MeditationMovement}
          element={
            <MeditationMovement nextRoute={RoutePaths.MeditationBalance} />
          }
        />
        <Route
          path={RoutePaths.MeditationBalance}
          element={<MeditationBalance nextRoute={RoutePaths.MeditationMind} />}
        />
        <Route
          path={RoutePaths.MeditationMind}
          element={<MeditationMind nextRoute={RoutePaths.MeditationSleep} />}
        />
        <Route
          path={RoutePaths.MeditationSleep}
          element={<MeditationSleep nextRoute={RoutePaths.MeditationEnergy} />}
        />
        <Route
          path={RoutePaths.MeditationEnergy}
          element={
            <MeditationEnergy nextRoute={RoutePaths.MeditationFatigue} />
          }
        />
        <Route
          path={RoutePaths.MeditationFatigue}
          element={<MeditationFatigue nextRoute={RoutePaths.MeditationPlan} />}
        />
        <Route
          path={RoutePaths.MeditationPlan}
          element={<MeditationPlan nextRoute={RoutePaths.MeditationThankYou} />}
        />
        <Route
          path={RoutePaths.GiftCard}
          element={<GiftCard locale={locale} nextRoute={RoutePaths.ThankYou} />}
        />
        <Route
          path={RoutePaths.ThankYou}
          element={<StripeThankYou locale={locale} />}
        />
        <Route path={RoutePaths.MeditationThankYou} element={<ThankYou />} />
        <Route
          path={RoutePaths.CancellationRequestSuccess}
          element={<CancellationRequestSuccess />}
        />
      </Route>
      <Route
        element={
          <ProtectedRoute
            user={user}
            redirectPath={cancellationRedirectPath()}
          />
        }>
        <Route
          path={RoutePaths.CancellationRequest}
          element={<CancellationRequest locale={locale} />}
        />
      </Route>
      {/* Currently unused routes */}
      <Route
        path={RoutePaths.FeedbackPersonalize}
        element={<FeedbackPersonalize />}
      />
      {/* For any other route, redirect to root path */}
      <Route path="*" element={<Navigate to={RoutePaths.Root} />} />
    </Routes>
  )
}
